import React, {useEffect, useRef} from 'react';
import {Button, FormControl, Popover} from 'react-bootstrap';

import {SB_ENTER_KEY_CODE} from '../../../../../Constants';
import {KeyboardUtils} from '../../../../../Utils';
import {Glyphicon} from '../../../../Common/GlyphIcon';

export const EditColumnPopover = React.forwardRef(({controller}, ref) => {
    const {
        filters,
        filterMethods,
        groupingMethods,
        useGroupBy,
        title,
        onTitleChange,
        addFilter,
        updateFilterMethod,
        updateFilterValue,
        removeFilter,
        groupingValue,
        onGroupingChange,
        onDeleteColumn,
        onCancel,
        onSave,
    } = controller;

    const saveButtonRef = useRef(null);
    const lastFilterInputRef = useRef(null);
    const previousFilterCountRef = useRef(filters?.length ?? 0);

    useEffect(() => {
        const handleKeyDown = (event) => {
            KeyboardUtils.handleKeyPress(SB_ENTER_KEY_CODE, event, () => {
                saveButtonRef.current?.focus?.();
                saveButtonRef.current?.click?.();
            });
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        const previousCount = previousFilterCountRef.current;
        const currentCount = filters?.length ?? 0;

        if (currentCount > previousCount) {
            // A new filter was added, focus its input if available
            setTimeout(() => {
                lastFilterInputRef.current?.focus?.();
            }, 150);
        }

        previousFilterCountRef.current = currentCount;
    }, [filters]);

    const renderFilterRows = () => {
        if (!filters) return null;

        return filters.map((filter, index) => (
            <div key={filter.guid} className="filter-column">
                <FormControl
                    as="select"
                    value={filter.filterMethodId}
                    onChange={(e) => updateFilterMethod(filter.guid, e.target.value)}
                >
                    {filterMethods.map(method => (
                        <option key={method.filterMethodId} value={method.filterMethodId}>{method.name}</option>
                    ))}
                </FormControl>
                {filter.hasValue ? (
                    <FormControl
                        ref={index === filters.length - 1 ? lastFilterInputRef : undefined}
                        type="text"
                        value={filter.value}
                        placeholder={filter.placeholderText}
                        onChange={(e) => updateFilterValue(filter.guid, e.target.value)}
                    />
                ) : null}
                <div className="remove-table">
                    <a className="pndlm-remove-control" onClick={() => removeFilter(filter.guid)}>
                        <Glyphicon glyph="remove-sign" />
                    </a>
                </div>
            </div>
        ));
    };

    const renderGroupingSelect = () => {
        if (!useGroupBy) {
            return null;
        }

        const currentGrouping = groupingValue ?? '';
        return (
            <div className="popover-content-block">
                <label className="sb-query-input-labels">Grouping Method</label>
                <FormControl
                    as="select"
                    value={currentGrouping}
                    onChange={(e) => onGroupingChange(e.target.value)}
                >
                    <option value=''>Group by This Column</option>
                    {groupingMethods.map(method => (
                        <option key={method.groupingMethodId} value={method.groupingMethodId}>
                            {method.name}
                        </option>
                    ))}
                </FormControl>
            </div>
        );
    };

    return (
        <Popover
            ref={ref}
            id={`sb-query-column-editor-popup-${controller?.columnGuid ?? 'unknown'}`}
            placement="bottom"
            style={{left: 0, top: '70px', minWidth: '100%'}}
        >
            <div className="popover-title p-0">
                <div className="filter-title" onClick={addFilter}>
                    <div>
                        <Glyphicon glyph="filter" />
                        <span>Add a Filter</span>
                    </div>
                    <div className="add-table">
                        <a className="pndlm-remove-control">
                            <Glyphicon glyph="plus-sign" />
                        </a>
                    </div>
                </div>
            </div>
            <div className="popover-content">
                {renderFilterRows()}
                <div className="popover-content-block">
                    <label className="sb-query-input-labels">Column Title</label>
                    <FormControl
                        type="text"
                        value={title}
                        onChange={(e) => onTitleChange(e.target.value)}
                    />
                </div>
                {renderGroupingSelect()}
            </div>
            <div className="popover-footer btn-edit justify-center">
                <Button variant="delete" onClick={onDeleteColumn}>Delete Column</Button>
                <Button variant="cancel" onClick={onCancel}>Cancel</Button>
                <Button
                    variant="save"
                    onClick={onSave}
                    ref={saveButtonRef}
                >
                    Save
                </Button>
            </div>
        </Popover>
    );
});
